import { $, $$ } from "@utils/dom";
import { on, off } from "@utils/listener";
import { getMessage } from "@core/locales";
import GeoLocation from "@core/geolocation";

const SORTED_CLASSNAME = "--js-sorted";

async function loadAPI(callback) {
  if( !google.maps.geometry ) await google.maps.importLibrary("geometry");
  if( !google.maps.Geocoder ) await google.maps.importLibrary("geocoding");

  callback();
};

class RestaurantsSelector {
  constructor(el, emitter){
    this.el = el;
    this.emitter = emitter;

    this.toggler = $('.gfield_label', this.el);
    this.container = $('.ginput_container', this.el);
    this.checkboxWrap = $('.gfield_checkbox', this.el);
    this.checkboxes = [...$$('input[type="checkbox"]', this.el)];
    this.restaurants = [...$$('.gchoice', this.el)].map(restaurant => new Restaurant(restaurant));

    this._opened = false;
    this._expanded = false;
    this._initialLabel = this.toggler ? this.toggler.innerHTML : getMessage('choose-restaurants');
    this._selectedValues = [];

    this.center = null;

    this._toggleOpened = this._toggleOpened.bind(this);
    this._toggleExpanded = this._toggleExpanded.bind(this);
    this._setValues = this._setValues.bind(this);
    this._onGeoLocationChange = this._onGeoLocationChange.bind(this);
    this._onClickOutside = this._onClickOutside.bind(this);

    this.init();
  }

  init(){
    if( GeoLocation.latitude && GeoLocation.longitude ) {
      this.center = GeoLocation.position;
      this.el.classList.add(SORTED_CLASSNAME);
    } else {
      GeoLocation.request();
    }

    this._appendExpandBtn();
    this._bindEvents();
    this._setValues();

    loadAPI( this._onAPILoaded.bind(this) );
  }

  destroy(){
    this._unbindEvents();

    if( this.restaurants ) this.restaurants.forEach(restaurant => restaurant.destroy());

    this.el = null;
    this.emitter = null;

    this.toggler = null;
    this.container = null;
    this.checkboxWrap = null;
    this.checkboxes = null;
    this.restaurants = null;
    this.expandBtn = null;

    this.center = null;

    this._opened = null;
    this._expanded = null;
    this._initialLabel = null;
    this._selectedValues = null;

    this._toggleOpened = null;
    this._toggleExpanded = null;
    this._setValues = null;
    this._onGeoLocationChange = null;
    this._onClickOutside = null;
  }

  _bindEvents(){
    GeoLocation.on('change', this._onGeoLocationChange);
    if (this.toggler) on(this.toggler, 'click', this._toggleOpened);
    if (this.expandBtn) on(this.expandBtn, 'click', this._toggleExpanded);
    if (this.checkboxes) on(this.checkboxes, 'change', this._setValues);
  }

  _unbindEvents(){
    GeoLocation.off('change', this._onGeoLocationChange);
    if (this.toggler) off(this.toggler, 'click', this._toggleOpened);
    if (this.expandBtn) off(this.expandBtn, 'click', this._toggleExpanded);
    if (this.checkboxes) off(this.checkboxes, 'change', this._setValues);

    off(window, "click", this._onClickOutside);
  }

  _toggleOpened(){
    this.opened = !this.opened;

    off(window, "click", this._onClickOutside);

    if (this.opened) {
     setTimeout(() => on(window, "click", this._onClickOutside), 1);
    }
  }

  _onClickOutside(event){
   if (event.target.closest(`#${this.el.id}`) == this.el) return;

   off(window, "click", this._onClickOutside);

   this.opened = false;
  }

  _toggleExpanded(){
    this.expanded = !this.expanded;
  }

  _onAPILoaded() {
    // sort restaurants by distance from Map's center
    if( this.center ) this._sortRestaurants(this.center);
  }

  _onGeoLocationChange() {
    // save center
    this.center = GeoLocation.position;

    // set class to indicate that restaurants are sorted
    this.el.classList.add(SORTED_CLASSNAME);

    // sort restaurants by distance from Map's center
    this._sortRestaurants(this.center);
  }

  _sortRestaurants(position){
    // calculate distance of all restaurants from center
    this.restaurants.forEach(restaurant => { restaurant.distance = google.maps.geometry.spherical.computeDistanceBetween(position, restaurant.position); });

    // sort by distance
    this.restaurants.sort((a, b) => a.distance - b.distance);

    // update DOM
    this.restaurants.forEach(restaurant => { this.checkboxWrap.appendChild(restaurant.el); });
    this.el.classList.add(SORTED_CLASSNAME);
  }

  _setValues(){
    let values = [];
    let label = '';

    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) values.push(checkbox.value);
    });

    this._selectedValues = values;

    this.filled = this._selectedValues.length ? true : false;

    if (this._selectedValues.length) {
      if (this._selectedValues.length > 1) {
        label = getMessage('restaurants-selected').replace('%s', this._selectedValues.length);
      } else {
        label = this._selectedValues[0];
      }
    } else {
      label = this._initialLabel;
    }

    if (this.toggler) this.toggler.innerHTML = label;
  }

  _appendExpandBtn(){
    this.expandBtn = document.createElement('span');
    this.expandBtn.classList.add('expand-btn');
    this.expandBtn.setAttribute('aria-hidden', true);

    const label = document.createElement('span');
    label.innerText = getMessage('view-more');

    this.expandBtn.append(label);
    this.container ? this.container.appendChild(this.expandBtn) : this.el.appendChild(this.expandBtn);
  }

  set opened(value){
    this._opened = value;

    this.el.classList[this.opened ? 'add' : 'remove']('--js-open');
    this.emitter.emit('SiteScroll.update');
  }

  get opened(){
    return this._opened;
  }

  set filled(value){
    this._filled = value;

    this.el.classList[this.filled ? 'add' : 'remove']('--filled');
  }

  get filled(){
    return this._filled;
  }

  set expanded(value){
    this._expanded = value;

    this.el.classList[this.expanded ? 'add' : 'remove']('--js-expanded');
    this.emitter.emit('SiteScroll.update');
  }

  get expanded(){
    return this._expanded;
  }
}

class Restaurant {
  constructor(el) {

    this.el = el;
    this.label = $('label', this.el);

    this.latitude = this.el.dataset.latitude;
    this.longitude = this.el.dataset.longitude;

    if( this.latitude ) this.latitude = parseFloat(this.latitude);
    if( this.longitude ) this.longitude = parseFloat(this.longitude);

    this.position = this.latitude && this.longitude ? { lat: this.latitude, lng: this.longitude } : null;

    this.init();
  }

  init() {
    this._bindEvents();
  }
  destroy() {
    this._unbindEvents();

    this.el = null;
    this.label = null;
    this.distanceEl = null;

    this.latitude = null;
    this.longitude = null;

    this._distance = null;
  }

  _bindEvents() {
  }
  _unbindEvents() {
  }

  _writeDistance(){
   if (!this.distanceEl) {
    this.distanceEl = document.createElement('span');
    this.distanceEl.classList.add('distance', 'color-light-red');
    this.label.appendChild(this.distanceEl);
   }

   this.distanceEl.innerText = `${(this._distance / 1000).toFixed(1)} km`;
  }

  // getter - setter
  get distance() { return this._distance; }
  set distance(value) {
    if( this._distance === value ) return;

    this._distance = value;
    this._writeDistance();
  }
}

export default RestaurantsSelector;
